import React from "react"
import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { cn } from "../lib/utils"
import { useSidebar } from "../hooks/use-layout"
import { Button } from "../components/ui/button"
import {
 DropdownMenu,
 DropdownMenuContent,
 DropdownMenuItem,
 DropdownMenuLabel,
 DropdownMenuSeparator,
 DropdownMenuTrigger,
} from "../components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar"
import { Separator } from "../components/ui/separator"
import {
 Menu,
 Maximize,
 Minimize,
 Moon,
 Sun,
 ChevronDown,
 LogOut,
 User,
 Settings,
 Home,
 Store,
 Trash2,
 RefreshCw,
 AlertTriangle,
 Minus,
 Plus,
 Languages,
} from "lucide-react"
import { useAuth } from "slices/user/hooks"
import BranchSwitcher from "Components/Common/BranchSwitcher"
import LanguageSwitcher, { APP_LANGUAGE_OPTIONS } from "Components/Common/LanguageSwitcher"
import { getCurrentAppLanguage, type AppLanguage } from "helpers/locale"
import NotificationDropdown from "Components/Common/NotificationDropdown"
import { useOnlineStatus } from "hooks/useOnlineStatus"
import { useOfflineBootstrapStatus } from "hooks/useOfflineBootstrapStatus"
import FailedOfflineOrdersDialog from "Components/Common/FailedOfflineOrdersDialog"
import FailedTableOpsDialog from "Components/Common/FailedTableOpsDialog"
import FailedMutationsDialog from "Components/Common/FailedMutationsDialog"
import { DeleteConfirmDialog } from "../components/delete-confirm-dialog"
import { resolveActiveNavLink } from "helpers/navActiveLink"
import { ConnectionStatusChip } from "../components/connection-status-chip"
import { Switch } from "../components/ui/switch"
import { QuickShortcutsMenu } from "../components/quick-shortcuts-menu"
import type { QuickShortcutNavigationItem } from "helpers/quickShortcuts"
import { useAutoUpdater } from "hooks/useAutoUpdater"
import { formatDateTimeShort } from "helpers/dateTime"
import { Loader2 } from "lucide-react"
import {
 persistUiZoom,
 readUiZoom,
 stepUiZoom,
 UI_ZOOM_MAX,
 UI_ZOOM_MIN,
 UI_ZOOM_STORAGE_KEY,
} from "helpers/uiZoom"
import { PAGE_VISITS_STORAGE_KEY } from "helpers/pageVisits"
interface HeaderProps {
 onToggleTheme?: () => void
 isDark?: boolean
 menuItems?: QuickShortcutNavigationItem[]
}

export function Header({ onToggleTheme, isDark = false, menuItems }: HeaderProps) {
 const { t, i18n } = useTranslation()
 const quickNavItems = React.useMemo(
 () => [
 { path:"/", label: t("Home"), icon: Home },
 { path:"/pos", label: t("POS title"), icon: Store },
 ],
 [t]
 )
 const { toggleMobile } = useSidebar()
 const location = useLocation()
 const [isFullscreen, setIsFullscreen] = React.useState(false)
 const { isOnline } = useOnlineStatus()
 const {
 status: offlineBootstrap,
 syncing: offlineSyncing,
 syncNow,
 summary: offlineSummary,
 refreshOfflineBootstrapStatus,
 } = useOfflineBootstrapStatus()

 const [failedOrdersOpen, setFailedOrdersOpen] = React.useState(false)
 const [failedTableOpsOpen, setFailedTableOpsOpen] = React.useState(false)
 const [failedMutationsOpen, setFailedMutationsOpen] = React.useState(false)

 const quickNavPaths = React.useMemo(
 () => quickNavItems.map((item) => item.path),
 [quickNavItems],
 )
 const activeQuickNavPath = React.useMemo(
 () => resolveActiveNavLink(quickNavPaths, location.pathname, location.search),
 [quickNavPaths, location.pathname, location.search],
 )
 const isActive = (path: string) => activeQuickNavPath === path

 const toggleFullscreen = () => {
 if (!document.fullscreenElement) {
 document.documentElement.requestFullscreen()
 setIsFullscreen(true)
 } else {
 document.exitFullscreen()
 setIsFullscreen(false)
 }
 }

 const { user, hasPermission, isSuperAdmin } = useAuth()
 const role = user?.roles?.[0] ?? user?.role ?? ''
 const isCashier = role === 'Cashier'|| role === 'cashier'
 const showTenantSettings = isSuperAdmin || hasPermission('manage_settings')
 const userName = user?.name ?? t('header.defaultUser')
 const userAvatar = user?.avatar
 const [showClearCache, setShowClearCache] = React.useState(false)
 const [clearingCache, setClearingCache] = React.useState(false)
 const [zoomPercent, setZoomPercent] = React.useState(readUiZoom)
 const currentLanguage = getCurrentAppLanguage()
 const setLanguage = (code: AppLanguage) => {
  if (code !== currentLanguage) {
   void i18n.changeLanguage(code)
  }
 }

 const isElectron = typeof window !== 'undefined' && !!(window as any).electronAPI
 const { checkForUpdates, status } = useAutoUpdater()
 const isChecking = status === 'checking'
 const offlineAttentionCount = offlineSummary.attentionCount
 const otherOpsAttention =
  (offlineSummary.failedMutations ?? 0) + (offlineSummary.mutationConflicts ?? 0)
 const syncDetailTitle = [
 offlineSummary.pendingForSync > 0
 ? t('header.syncPending', { count: offlineSummary.pendingForSync })
 : '',
 offlineSummary.failedOrders > 0
 ? t('header.syncFailed', { count: offlineSummary.failedOrders })
 : '',
 (offlineSummary.failedMutations ?? 0) > 0
 ? t('header.syncFailedOther', { count: offlineSummary.failedMutations })
 : '',
 offlineSummary.conflictCount > 0
 ? t('header.syncConflict', { count: offlineSummary.conflictCount })
 : '',
 ]
 .filter(Boolean)
 .join('\n')
 const lastOfflineSync = offlineBootstrap?.last_success_at
 ? formatDateTimeShort(offlineBootstrap.last_success_at)
 : t('header.syncNotCompletedYet')

 const handleClearCache = () => {
 setClearingCache(true)
 const preserved: Record<string, string> = {}
 for (const key of [UI_ZOOM_STORAGE_KEY, PAGE_VISITS_STORAGE_KEY]) {
  try {
   const value = localStorage.getItem(key)
   if (value != null) preserved[key] = value
  } catch { /* ignore */ }
 }
 localStorage.clear()
 for (const [key, value] of Object.entries(preserved)) {
  try {
   localStorage.setItem(key, value)
  } catch { /* ignore */ }
 }

 const reload = () => { window.location.reload() }

 const clearCachesAndReload = async () => {
 if ('caches' in window) {
 const cacheNames = await (window as any).caches.keys()
 await Promise.all(cacheNames.map((n: string) => (window as any).caches.delete(n)))
 }
 reload()
 }

 if (indexedDB.databases) {
 indexedDB.databases().then((dbs) => {
 dbs.forEach((db) => {
 if (db.name) indexedDB.deleteDatabase(db.name)
 })
 }).finally(clearCachesAndReload)
 } else {
 clearCachesAndReload()
 }
 }

 return (
 <header
 className={cn(
"app-header sticky top-[var(--app-banner-h,0px)] z-30 flex h-14 min-w-0 max-w-full items-center justify-between gap-3 border-b border-border-subtle bg-surface-header transition-[top,box-shadow] duration-200",
 /* Align with AppShell .page-wrapper — sidebar offset is on .app-content only */
"px-3 sm:px-4 lg:px-6")}
 >
 {/* RTL start (يمين): sidebar toggle + quick navigation */}
 <div className="flex min-h-0 min-w-0 items-center gap-2">
 <Button
 variant="ghost" size="icon-sm"
onClick={toggleMobile}
 aria-label={i18n.dir() === "rtl" ? "فتح القائمة الجانبية" : "Open sidebar"}
 title={i18n.dir() === "rtl" ? "فتح القائمة الجانبية" : "Open sidebar"}
 className="shrink-0 md:hidden">
 <Menu className="h-5 w-5"/>
 </Button>

 <nav
 className="hidden min-w-0 items-center gap-1.5 overflow-x-auto [scrollbar-width:thin] md:flex" dir={i18n.dir()}
 >
 {quickNavItems
 .filter((item) => {
 if (isCashier && item.path !== "/pos") return false
 return true
 })
 .map((item) => {
 const active = isActive(item.path)
 return (
 <Link
 key={item.path}
 to={item.path}
 className={cn(
"flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200 hover:-translate-y-px hover:shadow-sm active:translate-y-0",
 active
 ? "bg-primary-soft-strong/90 text-primary-soft-foreground font-semibold ring-1 ring-inset ring-primary-soft-border/70 shadow-sm dark:bg-[hsl(var(--brand-accent)_/_0.14)] dark:text-foreground dark:ring-[hsl(var(--brand-accent)_/_0.35)]":"text-muted-foreground hover:bg-primary-soft/60 hover:text-primary")}
 >
 <item.icon
 className={cn(
"h-4 w-4 shrink-0",
 active ? "text-primary-soft-foreground dark:text-[hsl(var(--brand-accent))]":"text-icon")}
 />
 <span className="whitespace-nowrap">{item.label}</span>
 </Link>
 )
 })}
 </nav>
 </div>

 {/* RTL end (شمال): branch, status, utilities, profile */}
 <div className="flex min-w-0 shrink items-center gap-1.5 sm:gap-2">
            {/* Mobile: icon-only BranchSwitcher must not shrink into the status chip */}
            <div className="shrink-0 sm:w-[clamp(12rem,22vw,22.5rem)] sm:min-w-0 sm:shrink">
              <BranchSwitcher />
            </div>

 <div className="hidden shrink-0 items-center sm:flex">
 <div className="app-header__tool-cluster flex items-center overflow-hidden rounded-xl">
 <ConnectionStatusChip
 online={isOnline}
 labelWhenOnline={t('Online')}
 labelWhenOffline={t('Offline')}
 showLabelFrom="sm"
 className="h-8 min-w-[2.75rem] rounded-none px-2.5 sm:min-w-[4.5rem] sm:px-3"
 />
 <Button
 type="button" variant="ghost" size="sm" disabled={!isOnline || offlineSyncing}
 onClick={() => { void syncNow() }}
 className={cn(
 'h-8 shrink-0 gap-1.5 rounded-none border-s border-border-subtle px-2.5 text-[11px] font-semibold hover:bg-muted/80',
 offlineAttentionCount > 0 && 'text-warning hover:bg-warning/10 hover:text-warning',
 )}
 title={`${t('header.syncNowTitle', { lastSync: lastOfflineSync })}${syncDetailTitle ? `\n${syncDetailTitle}` : ''}`}
 >
 {offlineSyncing ? (
 <RefreshCw className="h-3.5 w-3.5 shrink-0 animate-spin"/>
 ) : offlineSummary.failedOrders > 0 || offlineSummary.conflictCount > 0 ? (
 <AlertTriangle className="h-3.5 w-3.5 shrink-0"/>
 ) : (
 <RefreshCw className="h-3.5 w-3.5 shrink-0"/>
 )}
 <span className="hidden whitespace-nowrap md:inline">{t('header.syncNow')}</span>
 {offlineAttentionCount > 0 && (
 <span className="rounded-full border border-semantic-warning-border bg-semantic-warning-soft px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-semantic-warning">
 {offlineAttentionCount}
 </span>
 )}
 </Button>
 <DropdownMenu>
 <DropdownMenuTrigger asChild>
 <Button
 type="button" variant="ghost" size="sm" className="h-8 shrink-0 rounded-none border-s border-border-subtle px-2 hover:bg-muted/80" title={t('header.syncMenuTitle')} aria-label={t('header.syncMenuAriaLabel')}>
 <ChevronDown className="h-3.5 w-3.5"/>
 </Button>
 </DropdownMenuTrigger>
 <DropdownMenuContent align="end" dir={i18n.dir()} className="min-w-[14rem]">
 <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
 {t('header.offlineBootstrapStatus')}
 </DropdownMenuLabel>
 <DropdownMenuItem
 className="cursor-pointer"
onClick={() => { void syncNow() }}
 disabled={!isOnline || offlineSyncing}
 >
 <RefreshCw className="h-3.5 w-3.5 shrink-0"/>
 {t('header.syncNow')}
 </DropdownMenuItem>
 <DropdownMenuSeparator />
 <DropdownMenuItem
 className="cursor-pointer" disabled={offlineSummary.failedPosOrders === 0}
 onClick={() => setFailedOrdersOpen(true)}
 >
 <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning"/>
 {t('header.failedPosOrders')}
 {offlineSummary.failedPosOrders > 0 && (
 <span className="me-auto text-[10px] font-bold text-warning">
 {offlineSummary.failedPosOrders}
 </span>
 )}
 </DropdownMenuItem>
 <DropdownMenuItem
 className="cursor-pointer" disabled={offlineSummary.failedTableOps === 0}
 onClick={() => setFailedTableOpsOpen(true)}
 >
 <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning"/>
 {t('header.failedTableOps')}
 {offlineSummary.failedTableOps > 0 && (
 <span className="me-auto text-[10px] font-bold text-warning">
 {offlineSummary.failedTableOps}
 </span>
 )}
 </DropdownMenuItem>
 <DropdownMenuItem
 className="cursor-pointer" disabled={otherOpsAttention === 0}
 onClick={() => setFailedMutationsOpen(true)}
 >
 <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning"/>
 {t('header.failedOtherOps')}
 {otherOpsAttention > 0 && (
 <span className="me-auto text-[10px] font-bold text-warning">
 {otherOpsAttention}
 </span>
 )}
 </DropdownMenuItem>
 </DropdownMenuContent>
 </DropdownMenu>
 </div>
 </div>

 <Separator orientation="vertical" className="mx-0.5 hidden h-5 shrink-0 opacity-60 sm:block"/>

	 <div className="app-header__tool-cluster flex shrink-0 items-center gap-0.5 rounded-2xl p-1 sm:gap-1 [&>button]:rounded-xl [&>button]:transition-all [&>button:hover]:-translate-y-px [&>button:hover]:bg-primary-soft/70 [&>button:hover]:text-primary [&>button:hover]:shadow-sm [&>button:active]:translate-y-0">
	 <QuickShortcutsMenu menuItems={menuItems} />

	 <NotificationDropdown />

 <LanguageSwitcher className="hidden sm:inline-flex" />

 <Button
 variant="ghost" size="icon-sm"
onClick={onToggleTheme}
	 className="hidden shrink-0 rounded-xl transition-colors hover:bg-primary-soft/70 hover:text-primary focus-visible:ring-primary/25 sm:inline-flex">
 {isDark ? <Sun className="h-4 w-4"/> : <Moon className="h-4 w-4"/>}
 </Button>

 <Button
 variant="ghost" size="icon-sm"
onClick={toggleFullscreen}
	 className="hidden shrink-0 rounded-xl transition-colors hover:bg-primary-soft/70 hover:text-primary focus-visible:ring-primary/25 sm:flex">
 {isFullscreen ? (
 <Minimize className="h-4 w-4"/>
 ) : (
 <Maximize className="h-4 w-4"/>
 )}
 </Button>
 </div>

 <Separator orientation="vertical" className="mx-0.5 hidden h-5 shrink-0 opacity-60 sm:block"/>

 <DropdownMenu>
 <DropdownMenuTrigger asChild>
	 <Button
	 variant="ghost" title={userName} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-transparent p-0 shadow-sm transition-all hover:-translate-y-px hover:border-border-subtle hover:bg-primary-soft/45 hover:shadow-md active:translate-y-0 sm:w-auto sm:gap-2 sm:px-2">
 <Avatar className="h-7 w-7">
 <AvatarImage src={userAvatar} />
 <AvatarFallback className="bg-primary text-white text-[10px] font-bold">
 {userName?.charAt(0) || "U"}
 </AvatarFallback>
 </Avatar>
	 <span className="hidden max-w-[140px] truncate text-sm font-semibold sm:block xl:max-w-[180px]">
 {userName}
 </span>
 <ChevronDown className="hidden h-3 w-3 shrink-0 text-icon sm:block"/>
 </Button>
 </DropdownMenuTrigger>
 <DropdownMenuContent
 align="end"
 dir={i18n.dir()}
 className="w-64 max-sm:w-[min(22.5rem,calc(100vw-0.75rem))] max-sm:max-h-[min(88dvh,46rem)] max-sm:overflow-y-auto bg-popover text-popover-foreground border-border"
 >
 <DropdownMenuLabel className="font-normal">
 <div className="flex items-center gap-3 text-start">
 <Avatar className="h-10 w-10 sm:hidden">
 <AvatarImage src={userAvatar} />
 <AvatarFallback className="bg-primary text-white text-sm font-bold">
 {userName?.charAt(0) || "U"}
 </AvatarFallback>
 </Avatar>
 <div className="flex min-w-0 flex-col space-y-1">
 <p className="truncate text-sm font-medium leading-none text-foreground">{userName}</p>
 <p className="text-xs leading-none text-muted-foreground capitalize">
 {role || "user"}
 </p>
 </div>
 </div>
 </DropdownMenuLabel>
 <DropdownMenuSeparator />
 <DropdownMenuItem asChild>
 <Link to="/profile" className="flex w-full items-center gap-2 cursor-pointer">
 <User className="h-4 w-4 shrink-0 text-muted-foreground"/>
 <span>{t('header.profile')}</span>
 </Link>
 </DropdownMenuItem>
 {showTenantSettings ? (
 <DropdownMenuItem asChild>
 <Link to="/settings" className="flex w-full items-center gap-2 cursor-pointer">
 <Settings className="h-4 w-4 shrink-0 text-muted-foreground"/>
 <span>{t('header.tenantSettings')}</span>
 </Link>
 </DropdownMenuItem>
 ) : null}
 {isElectron && (
 <DropdownMenuItem
 onClick={checkForUpdates}
 disabled={isChecking}
 className="flex items-center gap-2 cursor-pointer"
 >
 {isChecking ? (
 <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"/>
 ) : (
 <RefreshCw className="h-4 w-4 shrink-0 text-muted-foreground"/>
 )}
 <span>{t('header.checkForUpdates')}</span>
 </DropdownMenuItem>
 )}
 <div className="sm:hidden">
 <DropdownMenuSeparator />
 <p className="px-2.5 pb-1.5 pt-1 text-[11px] font-semibold text-muted-foreground">
 {t('header.connection')}
 </p>
 <div
 className="mx-0.5 mb-1.5 flex items-center gap-2 rounded-xl border border-border-subtle bg-muted/40 p-2"
 onPointerDown={(event) => event.preventDefault()}
 >
 <ConnectionStatusChip
 online={isOnline}
 labelWhenOnline={t('Online')}
 labelWhenOffline={t('Offline')}
 showLabelFrom="always"
 className="min-w-0 flex-1 justify-center rounded-lg"
 />
 <button
 type="button"
 disabled={!isOnline || offlineSyncing}
 onClick={() => { void syncNow() }}
 title={t('header.syncNowTitle', { lastSync: lastOfflineSync })}
 className={cn(
 'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-border-subtle bg-background px-2.5 text-xs font-semibold shadow-sm transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40',
 offlineAttentionCount > 0 && 'border-warning/30 text-warning',
 )}
 >
 {offlineSyncing ? (
 <RefreshCw className="h-3.5 w-3.5 animate-spin"/>
 ) : (
 <RefreshCw className="h-3.5 w-3.5"/>
 )}
 {t('header.syncNow')}
 {offlineAttentionCount > 0 ? (
 <span className="rounded-full bg-semantic-warning-soft px-1.5 text-[10px] font-bold tabular-nums text-semantic-warning">
 {offlineAttentionCount}
 </span>
 ) : null}
 </button>
 </div>
 {offlineSummary.failedPosOrders > 0 ? (
 <DropdownMenuItem
 className="cursor-pointer"
 onClick={() => setFailedOrdersOpen(true)}
 >
 <AlertTriangle className="h-4 w-4 shrink-0 text-warning"/>
 <span>{t('header.failedPosOrders')}</span>
 <span className="ms-auto text-[10px] font-bold text-warning">
 {offlineSummary.failedPosOrders}
 </span>
 </DropdownMenuItem>
 ) : null}
 {offlineSummary.failedTableOps > 0 ? (
 <DropdownMenuItem
 className="cursor-pointer"
 onClick={() => setFailedTableOpsOpen(true)}
 >
 <AlertTriangle className="h-4 w-4 shrink-0 text-warning"/>
 <span>{t('header.failedTableOps')}</span>
 <span className="ms-auto text-[10px] font-bold text-warning">
 {offlineSummary.failedTableOps}
 </span>
 </DropdownMenuItem>
 ) : null}
 {otherOpsAttention > 0 ? (
 <DropdownMenuItem
 className="cursor-pointer"
 onClick={() => setFailedMutationsOpen(true)}
 >
 <AlertTriangle className="h-4 w-4 shrink-0 text-warning"/>
 <span>{t('header.failedOtherOps')}</span>
 <span className="ms-auto text-[10px] font-bold text-warning">
 {otherOpsAttention}
 </span>
 </DropdownMenuItem>
 ) : null}
 <DropdownMenuSeparator />
 <p className="px-2.5 pb-1.5 pt-1 text-[11px] font-semibold text-muted-foreground">
 {t('header.appearance')}
 </p>
 <div
 className="mx-0.5 space-y-2 rounded-xl border border-border-subtle bg-muted/40 p-2"
 onPointerDown={(event) => event.preventDefault()}
 >
 <div className="flex items-center justify-between gap-2 px-0.5">
 <span className="flex items-center gap-2 text-sm font-medium">
 <Languages className="h-4 w-4 text-muted-foreground"/>
 {t('header.language')}
 </span>
 </div>
 <div className="grid grid-cols-3 gap-1" dir="ltr">
 {APP_LANGUAGE_OPTIONS.map((option) => {
  const selected = currentLanguage === option.code
  return (
   <button
    key={option.code}
    type="button"
    onClick={() => setLanguage(option.code)}
    className={cn(
     'h-8 rounded-lg border text-[11px] font-semibold transition-colors',
     selected
      ? 'border-primary/30 bg-primary text-primary-foreground shadow-sm'
      : 'border-border-subtle bg-background text-foreground hover:bg-muted',
    )}
   >
    {option.code.toUpperCase()}
   </button>
  )
 })}
 </div>
 <button
 type="button"
 onClick={() => onToggleTheme?.()}
 className="flex w-full items-center justify-between gap-2 rounded-lg border border-border-subtle bg-background px-2.5 py-2 text-start text-sm font-medium shadow-sm"
 >
 <span className="flex items-center gap-2">
 {isDark ? <Sun className="h-4 w-4 text-muted-foreground"/> : <Moon className="h-4 w-4 text-muted-foreground"/>}
 {isDark ? t('header.lightMode') : t('header.darkMode')}
 </span>
 <Switch checked={isDark} className="pointer-events-none" />
 </button>
 </div>
 </div>
 <DropdownMenuSeparator className="max-sm:hidden" />
 <div
 className="mx-0.5 hidden flex-col gap-2 rounded-xl bg-muted/50 px-2.5 py-2 sm:flex"
 onPointerDown={(event) => event.preventDefault()}
 >
 <span className="text-xs font-medium text-muted-foreground">{t('header.zoom')}</span>
 <div className="grid grid-cols-[2rem_1fr_2rem] items-center gap-1.5" dir="ltr">
 <button
 type="button"
 aria-label={t('header.zoomOut')}
 disabled={zoomPercent <= UI_ZOOM_MIN}
 onClick={() => setZoomPercent(stepUiZoom(zoomPercent, -1))}
 className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border-subtle bg-background text-foreground shadow-sm transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
 >
 <Minus className="h-3.5 w-3.5"/>
 </button>
 <button
 type="button"
 aria-label={t('header.zoomReset')}
 title={t('header.zoomReset')}
 onClick={() => setZoomPercent(persistUiZoom(100))}
 className="h-8 rounded-lg border border-border-subtle bg-background text-sm font-semibold tabular-nums text-foreground shadow-sm transition-colors hover:bg-muted"
 >
 {zoomPercent}%
 </button>
 <button
 type="button"
 aria-label={t('header.zoomIn')}
 disabled={zoomPercent >= UI_ZOOM_MAX}
 onClick={() => setZoomPercent(stepUiZoom(zoomPercent, 1))}
 className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border-subtle bg-background text-foreground shadow-sm transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
 >
 <Plus className="h-3.5 w-3.5"/>
 </button>
 </div>
 </div>
 <DropdownMenuSeparator />
 <DropdownMenuItem
 onClick={() => setShowClearCache(true)}
 className="flex items-center gap-2 cursor-pointer text-warning focus:bg-warning/10 focus:text-warning">
 <Trash2 className="h-4 w-4 shrink-0"/>
 <span>{t('header.clearCache')}</span>
 </DropdownMenuItem>
 <DropdownMenuSeparator />
 <DropdownMenuItem asChild variant="destructive">
 <Link
 to="/logout" className="flex w-full items-center gap-2 cursor-pointer">
 <LogOut className="h-4 w-4 shrink-0 -scale-x-100"/>
 <span>{t('header.logout')}</span>
 </Link>
 </DropdownMenuItem>
 </DropdownMenuContent>
 </DropdownMenu>
 </div>

 <DeleteConfirmDialog
 open={showClearCache}
 onOpenChange={setShowClearCache}
 onConfirm={handleClearCache}
 title={t('header.clearCache')} description={t('header.clearCacheDescription')} confirmText={t('header.clearCache')} cancelText={t('header.cancel')} loading={clearingCache}
 />

 <FailedOfflineOrdersDialog
 open={failedOrdersOpen}
 onOpenChange={setFailedOrdersOpen}
 onCountsMayHaveChanged={refreshOfflineBootstrapStatus}
 />
 <FailedTableOpsDialog
 open={failedTableOpsOpen}
 onOpenChange={setFailedTableOpsOpen}
 onCountsMayHaveChanged={refreshOfflineBootstrapStatus}
 />
 <FailedMutationsDialog
 open={failedMutationsOpen}
 onOpenChange={setFailedMutationsOpen}
 onCountsMayHaveChanged={refreshOfflineBootstrapStatus}
 />
 </header>
 )
}
